import { useEffect } from 'react';
import _difference from 'lodash/difference';
import {
  gqlAuthToken,
  gqlSystemRoleCode as SYSTEM_ROLE,
  gqlAccountStatusEnum as ACCOUNT_STATUS,
} from '@minecraft.graphql-types';
import { TokenParsed, TokenApp } from '@minecraft.utils';
import { getEnvironment } from '@minecraft.environment';
import { SystemRoleCode } from '@minecraft.graphql-operations';
import { setHeapIdentity } from '@blocs.heap';
import { useRouter } from '@blocs.router';
import {
  parseJwt,
  setAccessToken,
  setRefreshToken,
  getAccessToken,
  getRefreshToken,
  deleteOnboardingToken,
} from '../utils';
import {
  REPS_URL,
  TALENT_URL,
  CASTING_URL,
  ROLES,
  QUERY_PARAMS,
  APPLICATIONS,
  AUDIENCE_TOKEN,
  ApplicationValue,
} from '../constants';

const getEnv = (key: string): string => {
  const environment = getEnvironment();
  const envValue = environment[key];

  return envValue || process.env[key];
};

export const getAppValueForTokenApp = (value?: TokenApp): ApplicationValue => {
  switch (value) {
    case 'talent':
      return APPLICATIONS.talent;
    case 'representative':
      return APPLICATIONS.reps;
    case 'casting':
      return APPLICATIONS.casting;
    default:
      return undefined;
  }
};

export const getAudience = (role: SYSTEM_ROLE | SystemRoleCode): ApplicationValue | null => {
  switch (role) {
    case SYSTEM_ROLE.TALENT:
      return APPLICATIONS.talent;
    case SYSTEM_ROLE.PROJECT_CREATOR:
    case SYSTEM_ROLE.CASTING_DIRECTOR:
    case SYSTEM_ROLE.STUDIO:
    case SYSTEM_ROLE.CREATIVE:
    case SYSTEM_ROLE.SHARED_PROJECT_USER:
      return APPLICATIONS.casting;
    case SYSTEM_ROLE.AGENT:
    case SYSTEM_ROLE.MANAGER:
      return APPLICATIONS.reps;
    default:
      return null;
  }
};

export const getUrlForAudience = (audience: ApplicationValue): string => {
  let url: string;

  switch (audience) {
    case APPLICATIONS.talent:
      url = getEnv(TALENT_URL);
      break;
    case APPLICATIONS.reps:
      url = getEnv(REPS_URL);
      break;
    case APPLICATIONS.casting:
      url = getEnv(CASTING_URL);
      break;
    case APPLICATIONS.admin:
      url = getEnv('ADMIN_URL');
      break;
    case APPLICATIONS['public-pages']:
      url = getEnv('PUBLIC_URL');
      break;
    case APPLICATIONS.login:
      url = getEnv('ULA_URL');
      break;
    default:
  }

  return url;
};

export const redirectToAudience = (audience: ApplicationValue, redirectTo = ''): void => {
  const url = getUrlForAudience(audience);
  const couponCode = audience === APPLICATIONS.talent ? localStorage.getItem(QUERY_PARAMS.couponCode) : undefined;

  // Next router can't redirect to external link, so using window here
  if (url) {
    const refreshToken = getRefreshToken();
    const queryParams = new URLSearchParams(redirectTo);

    if (couponCode) {
      queryParams.append(QUERY_PARAMS.couponCode, couponCode);
      localStorage.removeItem(QUERY_PARAMS.couponCode);
    }

    const generatedUrl = `${url}/authenticate/${refreshToken}?${queryParams.toString()}`;

    window.location.replace(generatedUrl);
  }
};

export const buildRedirectQueryParams = (
  query: URLSearchParams,
  organizationIds: number[],
  isFromOnboarding: boolean,
  parsedAudienceToken?: TokenParsed
) => {
  const queryRedirectUrl = query.get(QUERY_PARAMS.redirectUrl);
  const queryDivisionId = query.get(QUERY_PARAMS.divisionId);
  const requestTypeFromQuery = query.get(QUERY_PARAMS.requestType);
  const projectIdsFromQuery = query.get(QUERY_PARAMS.projectIds);
  const slotDateToFromQuery = query.get(QUERY_PARAMS.slotDateTo);
  const slotDateFromFromQuery = query.get(QUERY_PARAMS.slotDateFrom);
  const mediaQuestionDueDateToFromQuery = query.get(QUERY_PARAMS.dueDateTo);
  const mediaQuestionDueDateFromFromQuery = query.get(QUERY_PARAMS.dueDateFrom);
  const savedSearchId = query.get(QUERY_PARAMS.savedSearchId);
  const defaultAppContext = query.get(QUERY_PARAMS.defaultAppContext);

  let redirectTo = `?isFromOnboarding=${isFromOnboarding}`;

  // Keep redirect query params during auth.
  if (queryRedirectUrl) {
    redirectTo = `?redirectTo=${queryRedirectUrl}`;
    redirectTo = queryDivisionId ? `${redirectTo}&division_id=${queryDivisionId}` : redirectTo;
    redirectTo = requestTypeFromQuery ? `${redirectTo}&requestType=${requestTypeFromQuery}` : redirectTo;
    redirectTo = projectIdsFromQuery ? `${redirectTo}&projectIds=${projectIdsFromQuery}` : redirectTo;
    redirectTo = slotDateToFromQuery ? `${redirectTo}&slotDateTo=${slotDateToFromQuery}` : redirectTo;
    redirectTo = slotDateFromFromQuery ? `${redirectTo}&slotDateFrom=${slotDateFromFromQuery}` : redirectTo;
    redirectTo = mediaQuestionDueDateToFromQuery
      ? `${redirectTo}&dueDateTo=${mediaQuestionDueDateToFromQuery}`
      : redirectTo;
    redirectTo = mediaQuestionDueDateFromFromQuery
      ? `${redirectTo}&dueDateFrom=${mediaQuestionDueDateFromFromQuery}`
      : redirectTo;
    redirectTo = savedSearchId ? `${redirectTo}&savedSearchId=${savedSearchId}` : redirectTo;

    if (defaultAppContext) {
      // Preserve defaultAppContext query param if the user is an artist
      const tokenInfo = parseJwt(getAccessToken());

      if (tokenInfo?.artistId) {
        redirectTo = `${redirectTo}&default_app_context=${defaultAppContext}`;
      }
    }
  }

  if (Array.isArray(organizationIds) && Array.isArray(parsedAudienceToken?.organizations)) {
    const newOrganization = _difference(organizationIds, parsedAudienceToken.organizations)[0];

    redirectTo = newOrganization ? `${redirectTo}&organizationId=${newOrganization}` : redirectTo;
  }

  if (parsedAudienceToken) redirectTo = `${redirectTo}&isAdditionalAccount=true`;

  return redirectTo;
};

interface RedirectByRoleParams {
  token?: gqlAuthToken;
  selectedAudience?: SYSTEM_ROLE;
  audienceToken?: string;
  isFromOnboarding?: boolean;
  navigate: (options: { to: string }) => void;
  searchParams: URLSearchParams;
}

export const redirectByRole = ({
  navigate,
  searchParams,
  token,
  selectedAudience,
  audienceToken,
  isFromOnboarding,
}: RedirectByRoleParams) => {
  const queryRedirectAudience = searchParams.get(QUERY_PARAMS.redirectAudience);
  const parsed: TokenParsed = token?.access && parseJwt(token.access);
  const parsedAudienceToken: TokenParsed = audienceToken && parseJwt(audienceToken);

  if (!parsed || (audienceToken && !parsedAudienceToken)) return;

  setAccessToken(token.access);
  setRefreshToken(token.refresh);

  if (parsed.status === ACCOUNT_STATUS.UNVERIFIED) {
    const queryParams = new URLSearchParams({
      email: parsed.email,
    });

    navigate({ to: `/verify-email?${queryParams}` });

    return;
  }

  if (parsed.status === ACCOUNT_STATUS.INCOMPLETE) {
    const isPC = parsed?.roles?.includes(SYSTEM_ROLE.PROJECT_CREATOR);

    if (isPC) {
      navigate({ to: '/last-step' });

      return;
    }
  }

  if (isFromOnboarding) deleteOnboardingToken();

  setHeapIdentity(token?.access);

  const audience = selectedAudience
    ? getAudience(selectedAudience)
    : (String(queryRedirectAudience) as ApplicationValue);

  const availableAudience = parsed.roles.map((role) => getAudience(role));
  const redirectTo = buildRedirectQueryParams(
    searchParams,
    parsed?.organizations,
    isFromOnboarding,
    parsedAudienceToken
  );

  localStorage.setItem(ROLES, JSON.stringify(parsed.roles));
  localStorage.removeItem(AUDIENCE_TOKEN);

  if (audience && availableAudience.includes(audience)) {
    redirectToAudience(audience, redirectTo);
  } else if (availableAudience.length === 1) {
    redirectToAudience(availableAudience[0], redirectTo);
  } else if (
    availableAudience.length > 1 &&
    (parsed.roles.includes(SYSTEM_ROLE.AGENT) || parsed.roles.includes(SYSTEM_ROLE.MANAGER))
  ) {
    redirectToAudience('reps', redirectTo);
  } else {
    window.location.assign('/login/disambiguation');
  }
};

export type UseRedirectByRoleFn = (
  token?: gqlAuthToken,
  selectedAudience?: SYSTEM_ROLE,
  audienceToken?: string,
  isFromOnboarding?: boolean
) => void;

export const useRedirectByRole: UseRedirectByRoleFn = (
  token,
  selectedAudience,
  audienceToken,
  isFromOnboarding = false
) => {
  const { navigate, search } = useRouter();

  useEffect(() => {
    redirectByRole({
      token,
      navigate,
      searchParams: new URLSearchParams(search),
      selectedAudience,
      audienceToken,
      isFromOnboarding,
    });
  }, [token, audienceToken, selectedAudience, isFromOnboarding, navigate, search]);
};
